import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnInit,
    Output,
    Renderer2,
    signal,
} from "@angular/core";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { S25EditableAbstract } from "../../s25-editable/s25.editable.abstract";
import { S25LoadingApi } from "../../s25-loading/loading.api";
import { EventMicroService } from "../../../services/event.micro.service";
import { Debounce } from "../../../decorators/debounce.decorator";
import { S25Const } from "../../../util/s25-const";

export interface requirement extends DropDownItem {
    hasQuantity: boolean;
    quantity: number;
    hasComment: boolean;
    comment: string;
}

@TypeManagerDecorator("s25-ng-ev-req")
@Component({
    selector: "s25-ng-ev-req",
    template: `@if (init) {
        @if (addReqButton && !readOnly) {
            <button
                #addReqButton
                class="aw-button aw-button--primary c-margin-bottom--quarter"
                (click)="showDropdown()"
            >
                Add a Requirement
            </button>
        }
        @if (!addReqButton) {
            <s25-ng-dropdown-search-criteria
                [type]="'eventRequirements'"
                [(chosen)]="chosenReq"
                (chosenChange)="onChosen()"
            ></s25-ng-dropdown-search-criteria>
        }
        <s25-loading-inline [model]="{}"></s25-loading-inline>
        @for (item of items; track item) {
            <div class="evdd-req--flex">
                @if (!readOnly && showRemove) {
                    <div
                        class="ngBold"
                        id="requirement_delete_{{ item.itemName }}"
                        (click)="remove(item)"
                        (keyup.enter)="remove(item)"
                        tabindex="0"
                    >
                        <s25-ng-icon [type]="'close'"></s25-ng-icon>
                    </div>
                }
                <s25-ng-link-parser [text]="item.itemName" />
                @if (showQuantity && item.hasQuantity) {
                    <label class="evdd-req--quantity">
                        @if (showLabels) {
                            Quantity
                        }
                        <s25-ng-editable-number
                            [type]="'integer'"
                            [(val)]="item.quantity"
                            [alwaysEditing]="alwaysEditing"
                            [hasCommit]="!alwaysEditing"
                            [onCommit]="item.saveQuantity"
                            [readOnly]="readOnly"
                            [min]="0"
                            (valChange)="onItemsChange()"
                        >
                        </s25-ng-editable-number>
                    </label>
                }
                @if (showComment && item.hasComment && (item.comment || !readOnly)) {
                    <label class="evdd-req--comment">
                        @if (showLabels) {
                            Comment
                        }
                        <s25-ng-editable-textarea
                            [(val)]="item.comment"
                            [alwaysEditing]="alwaysEditing"
                            [hasCommit]="!alwaysEditing"
                            [onCommit]="item.saveComment"
                            [readOnly]="readOnly"
                            (valChange)="onItemsChange()"
                        >
                        </s25-ng-editable-textarea>
                    </label>
                }
            </div>
        }
        @if (errorMsg()) {
            <p class="ngRed">{{ errorMsg() }}</p>
        }
    }`,
    host: {
        "[class.bulk-edit]": "alwaysEditing",
    },
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 5px;

            & > button {
                width: fit-content;
            }

            .evdd-req--flex {
                display: flex;
                flex-wrap: wrap;
                align-items: flex-start;
                column-gap: 5px;
                row-gap: 10px;

                s25-ng-link-parser {
                    flex: 1;
                    position: relative;
                    top: 3px;
                    overflow-wrap: anywhere;
                }

                label {
                    display: flex;
                    align-items: center;

                    &.evdd-req--quantity {
                        margin-left: auto;
                        flex: 0 0 23%;
                        justify-content: space-between;
                        align-items: flex-start;
                        position: relative;
                        top: 3px;
                    }

                    &.evdd-req--comment {
                        flex-direction: column;
                        align-items: flex-start;
                        flex-basis: 100%; /* Make sure comments are on their own row */
                        margin-left: auto;
                        padding-left: 8%;

                        s25-ng-editable-textarea {
                            width: 100%;
                        }
                    }
                }
            }

            &.bulk-edit {
                s25-ng-link-parser {
                    flex-basis: 90% !important;
                }

                label {
                    &.evdd-req--quantity {
                        flex-direction: column;
                        margin-left: 15px !important;
                        flex: 0 !important;
                        position: static !important;
                    }

                    &.evdd-req--comment {
                        flex-basis: 0 !important;
                        margin-left: 15px !important;
                    }
                }
            }

            .ngRed {
                font-size: 90%;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EvRequirementsComponent extends S25EditableAbstract<any> implements OnInit {
    @Input() itemId: number;
    @Input() items: requirement[] = []; //requirements

    @Input() showDetails: boolean = true;

    @Input() showRemove: boolean = true;
    @Input() showComment: boolean = true;
    @Input() showQuantity: boolean = true;
    @Input() showLabels: boolean = true;
    @Input() addReqButton: boolean = true;
    @Input() updateCallback: (items: any) => void;
    init = false;
    chosenReq: any = null;
    errorMsg = signal("");

    @Output() itemsChange = new EventEmitter<any>();

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private zone: NgZone,
        private renderer: Renderer2,
    ) {
        super(elementRef, cd, zone, renderer);
    }

    getType = () => "eventRequirements";

    onItemsChange() {
        this.itemsChange.emit(this.items);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.items = S25Util.array.forceArray(this.items);
        this.getFields(this.items).then(() => {
            this.items.forEach((item) => {
                item.saveComment = (comment: string) => {
                    return this.save(item.itemId, comment, "comments");
                };
                item.saveQuantity = (quantity: string) => {
                    return this.save(item.itemId, quantity, "quantity");
                };
            });

            this.init = true;
            this.cd.markForCheck();
        });
    }

    updateView() {
        this.cd.markForCheck();
    }

    onChosen() {
        if (!this.chosenReq) return;
        let chosen = S25Util.deepCopy(this.chosenReq);
        this.chosenReq = {};
        return this.add(chosen);
    }

    async save(itemId: number | string, data: any, dataType: "quantity" | "comments") {
        if (dataType === "comments") data = data || "";
        if (dataType === "quantity") data = S25Util.toInt(data);
        const [_, err] = await S25Util.Maybe(
            EventMicroService.putEventRequirements(
                [{ op: "update", requirementId: +itemId, [dataType]: data }],
                this.itemId,
            ),
        );
        if (err) {
            return this.setErrorMsg(err);
        }
        this.errorMsg.set("");
    }

    async add(item: any) {
        if (S25Util.array.isIn(this.items, "itemId", item.itemId)) return;
        item.comment ??= "";
        S25LoadingApi.init(this.elementRef.nativeElement);
        await this.getFields(item);
        item.saveComment = (comment: string) => {
            return this.save(item.itemId, comment, "comments");
        };
        item.saveQuantity = (quantity: string) => {
            return this.save(item.itemId, quantity, "quantity");
        };
        if (!this.alwaysEditing) {
            const [_, err] = await S25Util.Maybe(
                EventMicroService.putEventRequirements([{ op: "add", requirementId: item.itemId }], this.itemId),
            );
            if (err) {
                S25LoadingApi.destroy(this.elementRef.nativeElement);
                return this.setErrorMsg(err);
            }
            this.errorMsg.set("");
        }
        S25LoadingApi.destroy(this.elementRef.nativeElement);
        this.alwaysEditing ? this.items.push(item) : this.items.unshift(item); // in event details, show new req at top of list - in bulk edit add to the bottom
        this.addReqButton = !this.alwaysEditing && true;
        this.cd.markForCheck();
        this.updateCallback && this.updateCallback(this.items);
        this.onItemsChange();
    }

    /*
    Determines whether quantity/comments should be displayed for each attribute.
     */
    async getFields(items: any) {
        return SearchCriteriaService.getEventRequirements(true, null, null).then((resp) => {
            items = S25Util.array.forceArray(items);
            items.forEach((item: any) => {
                let req = S25Util.array.getByProp(resp, "itemId", item.itemId);
                // item.hasRemove = !!req;
                item.hasQuantity = this.readOnly ? item.quantity > 0 : req?.quantity > -1;
                item.hasComment = !!req?.allowComment;
            });
            return items;
        });
    }

    @Debounce(500)
    async remove(item: any) {
        S25LoadingApi.init(this.elementRef.nativeElement);
        if (!this.alwaysEditing) {
            const [_, err] = await S25Util.Maybe(
                EventMicroService.putEventRequirements(
                    [
                        {
                            op: "update",
                            requirementId: item.itemId,
                        },
                    ],
                    this.itemId,
                    true,
                ),
            );
            if (err) {
                S25LoadingApi.destroy(this.elementRef.nativeElement);
                return this.setErrorMsg(err);
            }
            this.errorMsg.set("");
        }
        S25LoadingApi.destroy(this.elementRef.nativeElement);
        S25Util.array.inplaceRemoveByProp(this.items, "itemId", item.itemId);
        this.updateCallback && this.updateCallback(this.items);
        this.addReqButton = true;
        this.cd.markForCheck();
    }

    showDropdown() {
        this.addReqButton &&= false;
        this.cd.markForCheck();
    }

    setErrorMsg(errorData: any) {
        this.errorMsg.set(S25Util.errorText(errorData, S25Const.defaultErrorMessage));
        console.error(errorData);
    }
}
